import { Button, notify, SkeletonText, Text } from '@billy/design-system'
import { trackError } from '@billy/track-error'
import React, { ReactElement, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { NotificationKeys } from '../../../../enums/notificationKeys'
import { useOrganizationViewUrl } from '../../../../hooks/routing/useOrganizationViewUrl'
import { reactRoute } from '../../../../utils/routing/reactRoute'
import { widgetId } from '../../constants/widgetId'
import { Widget } from '../../elements/Widget'
import { useWidgetsActions } from '../../hooks/useWidgetsActions'
import { VatActionPoint } from './enums/vatActionPoint'
import { useVatPeriod } from './hooks/useVatPeriod'
import * as Styled from './styles'
import { TaxDeadlineDisplay } from './taxDeadlineDisplay/TaxDeadlineDisplay'
import { TaxDisplay } from './taxDisplay/TaxDisplay'

export const Vat = (): ReactElement | null => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const navigate = useNavigate()

  const { isLoading: isVatPeriodLoading, isError, vatDeclaration, vatReturnPeriod } = useVatPeriod()
  const { period, periodType, reportDeadline } = vatReturnPeriod ?? {}
  const { url: vatDeclarationsUrl } = useOrganizationViewUrl(reactRoute.organization.vatDeclarations.getMainRoute())

  const { trackDashboardActionPerformed } = useWidgetsActions()

  const isLoading = !organization || isVatPeriodLoading

  useEffect(() => {
    if (isError) {
      notify({
        id: NotificationKeys.DashboardWidgetVat,
        message: t('dashboard.widget.vat.error'),
        variant: 'error',
      })
      trackError('Could not load tax period')
    }
  }, [isError, t])

  const formatPeriod = () => {
    if (!period || !periodType) {
      return
    }

    const periodYearAndNumber = period.slice(period.lastIndexOf(':') + 1).split('-')

    if (periodType === 'annual') {
      return periodYearAndNumber[0]
    }

    const periodTypeLabel = t(`dashboard.widget.vat.period_type.${periodType}`)

    return `${periodYearAndNumber[periodYearAndNumber.length - 1]}. ${periodTypeLabel} ${periodYearAndNumber[0]}`
  }

  const settleVat = useCallback(() => {
    if (!vatReturnPeriod || !organization || !vatDeclarationsUrl) {
      return
    }

    trackDashboardActionPerformed({ action: VatActionPoint.SettleVat, widgetId: widgetId.Vat })
    navigate(vatDeclarationsUrl)
  }, [navigate, organization, trackDashboardActionPerformed, vatDeclarationsUrl, vatReturnPeriod])

  return (
    <Widget
      title={t('dashboard.widget.vat.title')}
      sideActions={
        vatReturnPeriod && (
          <Button onClick={settleVat} variant="outlined">
            {t('dashboard.widget.vat.settle_btn')}
          </Button>
        )
      }
    >
      <>
        <Styled.VatBody>
          <TaxDisplay
            amount={vatDeclaration?.totalAmount || 0}
            period={formatPeriod()}
            isLoading={isLoading}
            userHasToPayTax
          />
          {reportDeadline && <TaxDeadlineDisplay deadlineTimestamp={reportDeadline} isLoading={isLoading} />}
        </Styled.VatBody>

        <Styled.Footer>
          {isLoading ? (
            <SkeletonText variant="bodySM" />
          ) : (
            <Text variant="bodySM" colorVariant="secondary">
              {t('dashboard.widget.vat.explanation_footer')}
            </Text>
          )}
        </Styled.Footer>
      </>
    </Widget>
  )
}
